import { styled } from "@linaria/react";

import { PropsWithTheme, withTheme } from "@gx/dls/theme";
import { ProjectArtifactResource } from "@gx/ts-contracts/services/ontology/resources/artifact_pb";

import ArtifactContentViewer from "@/pages/projects/components/project/settings/artifacts/components/viewer/ArtifactContentViewer";
import ArtifactContentViewerHeader from "@/pages/projects/components/project/settings/artifacts/components/viewer/ArtifactContentViewerHeader";

import { useGetArtifactDownloadUrlMutation } from "@/api/mutations/ontology";

import { useDownloadFile } from "@/hooks/useDownloadFile";

interface ProjectArtifactContentViewerDrawerProps {
  artifact: ProjectArtifactResource;
  content: string | null;
  isLoading: boolean;
  error: Error | null;
  downloadUrl: string | null;
  onClose: () => void;
}

const ProjectArtifactContentViewerDrawerWrapper = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`);

const ProjectArtifactContentViewerDrawerHeaderWrapper = withTheme(styled.div<PropsWithTheme>`
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.color.background.tertiary};
  border-radius: 6px 6px 0 0;
  border-bottom: 0.5px solid ${({ theme }) => theme.color.border.primary};
  width: 100%;
  flex-shrink: 0;
`);

const ProjectArtifactContentViewerDrawerBody = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  width: 100%;
`);

const ProjectArtifactContentViewerDrawer = ({
  artifact,
  content,
  isLoading,
  error,
  downloadUrl,
  onClose,
}: ProjectArtifactContentViewerDrawerProps) => {
  const { mutate: getDownloadUrl } = useGetArtifactDownloadUrlMutation();

  const handleDownload = useDownloadFile({
    getUrl: () => void getDownloadUrl({ artifactUri: artifact.objectStorageUri }),
    filename: artifact.id,
    existingUrl: downloadUrl,
  });

  return (
    <ProjectArtifactContentViewerDrawerWrapper>
      <ProjectArtifactContentViewerDrawerHeaderWrapper>
        <ArtifactContentViewerHeader
          artifact={artifact}
          onDownload={handleDownload} // eslint-disable-line @typescript-eslint/no-misused-promises
          onClose={onClose}
        />
      </ProjectArtifactContentViewerDrawerHeaderWrapper>
      <ProjectArtifactContentViewerDrawerBody>
        <ArtifactContentViewer
          artifact={artifact}
          content={content}
          isLoading={isLoading}
          error={error}
        />
      </ProjectArtifactContentViewerDrawerBody>
    </ProjectArtifactContentViewerDrawerWrapper>
  );
};

export default ProjectArtifactContentViewerDrawer;
